import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { Avatar, ImageUploader, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { PlacePicker } from "@/components/PlacePicker";

// Evidence (approved sources) and Confirmations (members) are separate: 5 confirmations = "Confirmed by members", never "Verified".
const ConfirmDialog = ({ target, id, open, setOpen, onDone, testid }) => {
  const [note, setNote] = useState("");
  const [url, setUrl] = useState("");
  const [photos, setPhotos] = useState([]);
  const [where, setWhere] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/confirmations", { target, id, note, url, image: photos[0] || null, place: where?.place || "", lat: where?.lat ?? null, lng: where?.lng ?? null });
      if (data.accepted) { toast.success("Thanks, your confirmation was added"); onDone(data); setOpen(false); setNote(""); setUrl(""); setPhotos([]); setWhere(null); }
      else toast.error(`Middle couldn't add your confirmation: ${data.reason}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid={`${testid}-dialog`}>
        <DialogHeader><DialogTitle>Confirm this Fact</DialogTitle>
          <DialogDescription>Confirm only what you saw yourself or can establish. Say how you know. Middle checks your note fits the Fact before it counts.</DialogDescription></DialogHeader>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500} placeholder="e.g. I was at the station at 8am and saw the closure notice" data-testid={`${testid}-note`} />
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Link to your evidence (optional)" className="w-full rounded-xl border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-200" data-testid={`${testid}-url`} />
        <PlacePicker value={where} onChange={setWhere} testid={`${testid}-place`} />
        <ImageUploader value={photos} onChange={setPhotos} max={1} testid={`${testid}-photo`} />
        <p className="text-xs text-slate-400">Your note and link are shown with your name. A photo is only used for Middle's check.</p>
        <Button className="rounded-full" disabled={busy || note.trim().length < 15} onClick={send} data-testid={`${testid}-submit`}>{busy ? "Checking…" : "Add my confirmation"}</Button>
      </DialogContent>
    </Dialog>
  );
};

const ListDialog = ({ c, open, setOpen, testid }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!open) return;
    setRows(null);
    api.get(`/confirmations/${c.target}/${c.target_id}`).then((r) => setRows(r.data.items)).catch(() => setRows([]));
  }, [open, c.target, c.target_id, c.count]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid={`${testid}-list`}>
        <DialogHeader><DialogTitle>Member confirmations</DialogTitle>
          <DialogDescription>Members who saw or can establish the same thing. This is separate from Evidence from approved sources. {c.threshold} confirmations make a Fact “Confirmed by members”.</DialogDescription></DialogHeader>
        {rows === null ? <Spinner /> : !rows.length ? <p className="text-sm text-slate-500">No confirmations yet.</p> : (
          <ul className="max-h-[60vh] divide-y divide-border overflow-y-auto">{rows.map((r) => (
            <li key={r.user_id} className="flex gap-3 py-3 text-sm" data-testid={`${testid}-item-${r.user_id}`}>
              <Link to={`/u/${r.user_id}`}><Avatar user={r.user} size={32} /></Link>
              <div className="min-w-0 flex-1">
                <p>{r.user ? <Link to={`/u/${r.user_id}`} className="font-medium hover:underline">{r.user.name}</Link> : <span className="font-medium">Former member</span>} <span className="text-xs text-slate-400">{r.via === "post" ? "posted the same Fact" : "confirmed"} · {timeAgo(r.created_at)}</span></p>
                <p className="text-slate-600">{r.note}</p>
                {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer nofollow" className="break-all text-xs text-[hsl(var(--facts))] hover:underline">{r.url}</a>}
              </div>
            </li>))}</ul>)}
      </DialogContent>
    </Dialog>
  );
};

export const ConfirmBar = ({ target, id, c, onChange, testid = "confirm" }) => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(false);
  useEffect(() => {  // other cards showing the same Fact (repeat posts) pick up a new confirmation straight away
    const on = (e) => { if (c && e.detail.target_id === c.target_id && e.detail.count !== c.count) onChange?.({ ...c, count: e.detail.count, confirmed: e.detail.confirmed, nearby: e.detail.nearby, mine: true }); };
    window.addEventListener("middle:confirmed", on);
    return () => window.removeEventListener("middle:confirmed", on);
  }, [c, onChange]);
  if (!c) return null;
  const done = (d) => { onChange?.({ ...c, count: d.count, confirmed: d.confirmed, nearby: d.nearby ?? c.nearby, mine: true }); window.dispatchEvent(new CustomEvent("middle:confirmed", { detail: { target_id: c.target_id, count: d.count, confirmed: d.confirmed, nearby: d.nearby ?? c.nearby } })); };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <button type="button" onClick={(e) => { e.preventDefault(); setList(true); }} data-testid={`${testid}-count`}
        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${c.confirmed ? "border-sky-300 bg-sky-50 text-sky-800" : "border-slate-200 bg-white text-slate-600"}`}>
        <UsersRound size={12} />{c.confirmed ? `Confirmed by members · ${c.count}` : `${c.count} member ${c.count === 1 ? "confirmation" : "confirmations"}`}
        {c.nearby > 0 && <span className="border-l border-current/20 pl-1" title={`Confirmers who were within ${c.nearby_km} km of where this happened`} data-testid={`${testid}-nearby`}>{c.nearby} nearby</span>}
      </button>
      {c.can_confirm && !c.mine && <button type="button" onClick={(e) => { e.preventDefault(); setOpen(true); }} data-testid={`${testid}-open`} className="rounded-full border border-slate-300 px-2.5 py-0.5 text-[11px] font-semibold text-slate-700 hover:border-slate-900">I can confirm this</button>}
      {c.mine && <span className="text-[11px] text-sky-700" data-testid={`${testid}-mine`}>You confirmed this</span>}
      <ConfirmDialog target={target} id={id} open={open} setOpen={setOpen} onDone={done} testid={testid} />
      <ListDialog c={c} open={list} setOpen={setList} testid={testid} />
    </div>
  );
};

export const FirstReport = ({ fs, testid }) => {
  if (fs?.first_report !== "member") return null;
  const h = fs.lead_hours;
  const lead = h >= 48 ? `${Math.round(h / 24)} days` : h >= 1 ? `${Math.round(h)} ${Math.round(h) === 1 ? "hour" : "hours"}` : "minutes";
  return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={testid}><Clock size={11} />First reported by a Middle member, {lead} before approved sources</span>;
};
